import React, { PureComponent, createRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import convertPlainTextToHTML from '../../../../lib/convertPlainTextToHTML';

import TableDataCell from './TableDataCell';

const StyledTextarea = styled.textarea`
  width: 100%;
  height: 100%;
  border: 0px;
  border-radius: 0px;
  border-color: transparent;
  background-color: transparent;
  font-size: ${(props) => props.fontSize}px;
  color: ${(props) => props.fontColor};
  font-style: ${(props) => props.fontStyle};
  font-weight: ${(props) => props.fontWeight};
  text-align: ${(props) => props.textAlign};
  text-transform: ${(props) => props.textTransform};
  text-decoration: ${(props) => props.textDecoration};
  padding: ${(props) => props.padding}px;
  resize: none;

  border: 1px solid transparent;

  &:hover, &:focus, &:focus-visible {
    outline: unset;
    border: 1px solid transparent;
  }

`;

const StyledDisplayText = styled.div`
  ${(props) => (props.textOverflow ? `text-overflow: ${props.textOverflow};` : '')}
  ${(props) => (props.textOverflow === 'ellipsis' ? 'white-space: nowrap;' : '')}
  ${(props) => (props.textOverflow === 'ellipsis' ? 'overflow: hidden;' : '')}
  overflow-wrap: break-word;
`;

class TableFieldTextCell extends PureComponent {

  static propTypes = {
    id: PropTypes.string,
    disabled: PropTypes.bool,
    focused: PropTypes.bool,
    rowId: PropTypes.string,
    columnId: PropTypes.string,
    cellKey: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyPress: PropTypes.func,
    onTextareaRef: PropTypes.func, //Used only for testing

    //Styles
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    width: PropTypes.number,

  }

  constructor(props) {
    super(props);
    this.state = { changes: props.value };
    this.textareaRef = createRef();
    this.height = null;
    this.width = null;
  }

  componentWillUnmount() {

    const { onChange, focused, value, rowId, columnId, columnIdentifier, onBlur } = this.props;
    const { changes } = this.state;

    /**
     * DO NOT REMOVE
     *
     * When a component is unmounted the blur event doesn't get fired.
     * This ensure that inputs that rely on the blur event have their
     * changes saved even when react is unmounting them.
     *
     * See: https://github.com/zionlabs/Issues/issues/1205
     *
     */
    if (onChange && focused && changes !== value && onBlur) {

      onChange(rowId, columnId, changes);

      onBlur(rowId, columnId, columnIdentifier, {});

    }

  }

  moveCursorToEnd = () => {
    if (this.textareaRef.current) {
      const length = this.textareaRef.current.value.length;
      this.textareaRef.current.setSelectionRange(length, length);
    }
  };

  componentDidUpdate(prevProps) {
    const { value, focused } = this.props;

    if (prevProps.value !== value) {
      this.setState({ changes: value });
    }

    if (!prevProps.focused && focused) {
      this.moveCursorToEnd();
    }

  }

  render() {

    const {
      id,
      rowId,
      columnId,
      disabled,
      cellKey,
      focused,
      onFocus,
      onKeyPress,
      onTextareaRef,

      primaryColor,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderWidth,
      backgroundColor,
      padding,
      width,

    } = this.props;

    const { changes } = this.state;

    return focused && !disabled ? (
      <TableDataCell
        key={`${cellKey}-editable`}
        data-testid={`${id}-${rowId}-${columnId}-text-cell`}
        style={this.height ? { height: `${this.height}px`, minHeight: `${this.height}px` } : undefined}
        {...{
          focused,
          width,
          padding: 0,
          borderColor,
          borderWidth,
          backgroundColor,
          primaryColor,
          cellHeight: this.height
        }}
      >
        <StyledTextarea
          rows={1}
          autoFocus
          ref={(e) => {
            this.textareaRef.current = e;
            if (!e) return;
            if (onTextareaRef) onTextareaRef(e);

            e.focus();
          }}
          onKeyDown={(e) => {
            //Commented out because having these prevents the onChange from working properly
            //e.stopPropagation();
            // e.preventDefault();
            if (onKeyPress) onKeyPress(rowId, columnId, e);
          }}
          defaultValue={changes}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          onChange={this.handleChange}
          {...{
            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            textOverflow,
            padding
          }}
        />
      </TableDataCell>
    ) : (
      <TableDataCell
        key={`${cellKey}-preview`}
        disabled={disabled}
        data-testid={`${id}-${rowId}-${columnId}-text-cell`}
        handleCellRef={(e) => {
          /**
           * Used to make sure the rendered textarea cell
           * is the same size as the display text when
           * the text cell is focused.
           */
          if (e) {
            this.height = e.offsetHeight;
            this.width = e.offsetWidth;
          }
        }}
        onClick={() => {
          if (!disabled && onFocus) onFocus(rowId, columnId);
        }}
        {...{
          primaryColor,
          width,
          fontSize,
          fontColor,
          fontStyle,
          fontWeight,
          textAlign,
          textTransform,
          textDecoration,
          borderColor,
          borderWidth,
          backgroundColor,
          padding,
        }}
      >
        <StyledDisplayText
          {...{ textOverflow }}
          dangerouslySetInnerHTML={{ __html: this.getInnerHtml() }}
        />
      </TableDataCell>
    );

  }

  handleChange = (e) => {
    this.setState({ changes: e.target.value });
  }

  handleFocus = (e) => {

    const { onFocus, rowId, columnId, columnIdentifier, columnType, } = this.props;

    if (onFocus) {
      onFocus(rowId, columnId, columnIdentifier, columnType, { blur: () => { e.target.blur(); } });
    }

  }

  handleBlur = () => {

    const { changes } = this.state;
    const { value, onBlur, onChange, rowId, columnId, columnIdentifier } = this.props;

    if (onChange && changes !== value) onChange(rowId, columnId, changes);
    if (onBlur) onBlur(rowId, columnId, columnIdentifier, {});

  }

  getInnerHtml = () => {

    const { displayValue } = this.props;

    const parsedValue = displayValue && typeof displayValue !== 'string' ? displayValue.toString() : displayValue;

    return convertPlainTextToHTML(parsedValue);

  }

}

export default TableFieldTextCell;
